import React, { useState } from 'react';
import axios from 'axios';

const MobileTasksView = ({ tasks, setTasks, user, adminUsers, API_BASE }) => {
  const [filter, setFilter] = useState('all');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: 'medium',
    task_date: new Date().toISOString().split('T')[0]
  });

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'assigned', label: 'Assigned to Me' },
    { id: 'created', label: 'Created by Me' },
    { id: 'completed', label: 'Completed' }
  ];

  const getFilteredTasks = () => {
    let filtered = [...tasks];

    switch (filter) {
      case 'assigned':
        filtered = tasks.filter(t => t.assigned_to === user?.id && t.status !== 'completed');
        break;
      case 'created':
        filtered = tasks.filter(t => t.created_by === user?.id);
        break;
      case 'completed':
        filtered = tasks.filter(t => t.status === 'completed');
        break;
      default:
        filtered = tasks.filter(t => t.status !== 'completed');
    }

    return filtered;
  };

  const groupTasksByDate = (taskList) => {
    const today = new Date().toISOString().split('T')[0];
    const groups = {
      overdue: [],
      today: [],
      upcoming: [],
      completed: []
    };

    taskList.forEach(task => {
      if (task.status === 'completed') {
        groups.completed.push(task);
      } else {
        const taskDate = task.task_date?.substring(0, 10);
        if (taskDate < today) {
          groups.overdue.push(task);
        } else if (taskDate === today) {
          groups.today.push(task);
        } else {
          groups.upcoming.push(task);
        }
      }
    });

    return groups;
  };

  const handleToggleTask = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      const response = await axios.put(
        `${API_BASE}/calendar/tasks/${taskId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      setTasks(tasks.map(task => task.id === taskId ? response.data : task));
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleCreateTask = async () => {
    if (!taskForm.title.trim()) {
      alert('Task title is required');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE}/calendar/tasks`,
        taskForm,
        { withCredentials: true }
      );
      setTasks([...tasks, response.data]);
      setTaskForm({
        title: '',
        description: '',
        assigned_to: '',
        priority: 'medium',
        task_date: new Date().toISOString().split('T')[0]
      });
      setShowTaskModal(false);
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task');
    }
  };

  const filteredTasks = getFilteredTasks();
  const groupedTasks = groupTasksByDate(filteredTasks);

  const formatDueDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) return 'Overdue';
    if (date.toDateString() === today.toDateString()) return 'Today';
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const renderTaskGroup = (title, taskList, className = '') => {
    if (taskList.length === 0) return null;

    return (
      <div className="task-group">
        <div className="task-group-header">{title}</div>
        {taskList.map(task => (
          <div
            key={task.id}
            className={`mobile-task-card ${task.status === 'completed' ? 'completed' : ''}`}
          >
            <div
              className={`mobile-task-checkbox ${task.status === 'completed' ? 'checked' : ''}`}
              onClick={() => task.assigned_to === user?.id && handleToggleTask(task.id, task.status)}
            >
              {task.status === 'completed' && '✓'}
            </div>
            <div className="mobile-task-content">
              <div className="mobile-task-title">{task.title}</div>
              <div className="mobile-task-meta">
                <span className={`mobile-task-due ${task.task_date < new Date().toISOString().split('T')[0] && task.status !== 'completed' ? 'overdue' : ''}`}>
                  {formatDueDate(task.task_date)}
                </span>
                <span className={`task-priority ${task.priority}`}>{task.priority}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="mobile-tasks-view">
      {/* Header */}
      <div className="mobile-module-header">
        <div className="module-header-left">
          <div className="module-icon tasks">✓</div>
          <span className="module-title">Tasks</span>
        </div>
        <button className="add-btn" onClick={() => setShowTaskModal(true)}>+</button>
      </div>

      {/* Filter Tabs */}
      <div className="filter-tabs">
        {filters.map(f => (
          <button
            key={f.id}
            className={`filter-tab ${filter === f.id ? 'active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Task List */}
      <div className="mobile-task-list">
        {filteredTasks.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">✓</div>
            <div className="empty-state-title">No tasks</div>
            <div className="empty-state-text">You're all caught up!</div>
          </div>
        ) : (
          <>
            {renderTaskGroup('Overdue', groupedTasks.overdue)}
            {renderTaskGroup('Today', groupedTasks.today)}
            {renderTaskGroup('Upcoming', groupedTasks.upcoming)}
            {filter === 'completed' && renderTaskGroup('Completed', groupedTasks.completed)}
          </>
        )}
      </div>

      {/* Task Creation Modal */}
      {showTaskModal && (
        <div className="modal-overlay" onClick={() => setShowTaskModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Task</h3>
              <button className="modal-close" onClick={() => setShowTaskModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Task Title *</label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="Enter task title"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Enter task description"
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Due Date</label>
                  <input
                    type="date"
                    value={taskForm.task_date}
                    onChange={(e) => setTaskForm({ ...taskForm, task_date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Assign To</label>
                <select
                  value={taskForm.assigned_to}
                  onChange={(e) => setTaskForm({ ...taskForm, assigned_to: e.target.value })}
                >
                  <option value="">Select a user</option>
                  {adminUsers.map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.name || `User ${admin.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowTaskModal(false)}>Cancel</button>
              <button className="btn-create" onClick={handleCreateTask}>Create Task</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileTasksView;
